import { useState, useCallback } from "react";
import { pdfjs } from "react-pdf";
import { OCRErrorLog } from "types";
import dayjs from "dayjs";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDF_IMAGE_OPS = new Set(
  [
    pdfjs.OPS.paintImageXObject,
    pdfjs.OPS.paintInlineImageXObject,
    pdfjs.OPS.paintImageMaskXObject,
    (pdfjs.OPS as Record<string, number>).paintJpegXObject,
    (pdfjs.OPS as Record<string, number>).paintImageXObjectRepeat,
    (pdfjs.OPS as Record<string, number>).paintImageMaskXObjectRepeat,
    (pdfjs.OPS as Record<string, number>).paintImageMaskXObjectGroup,
  ].filter((op) => typeof op === "number"),
);

export const useOCRValidation = () => {
  const [ocrErrorLogs, setOcrErrorLogs] = useState<OCRErrorLog[]>([]);
  const [showOcrErrors, setShowOcrErrors] = useState(false);

  const pdfContainsImage = useCallback(async (file: File): Promise<boolean> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const operatorList = await page.getOperatorList();

      for (let i = 0; i < operatorList.fnArray.length; i++) {
        if (PDF_IMAGE_OPS.has(operatorList.fnArray[i])) {
          return true;
        }
      }
    }

    return false;
  }, []);

  const docxContainsImage = useCallback(
    async (file: File): Promise<boolean> => {
      const arrayBuffer = await file.arrayBuffer();
      const JSZip = (await import("jszip")).default;
      const zip = await JSZip.loadAsync(arrayBuffer);

      let hasImage = false;
      zip.forEach((relativePath, zipEntry) => {
        if (hasImage || zipEntry.dir) {
          return;
        }
        const path = relativePath.replace(/\\/g, "/").toLowerCase();
        if (path.startsWith("word/media/") || path.startsWith("word/embeddings/")) {
          hasImage = true;
        }
      });

      return hasImage;
    },
    [],
  );

  const checkDocumentRequiresOCR = useCallback(
    async (file: File): Promise<boolean> => {
      const fileName = file.name.toLowerCase();
      const type = (file.type || "").toLowerCase();

      if (type.startsWith("image/") || /\.(jpg|jpeg|png|gif|bmp|tiff|tif|webp)$/i.test(fileName)) {
        return true;
      }

      try {
        if (type.includes("pdf") || fileName.endsWith(".pdf")) {
          return await pdfContainsImage(file);
        }

        if (type.includes("wordprocessingml") || fileName.endsWith(".docx")) {
          return await docxContainsImage(file);
        }

        // Legacy .doc cannot be inspected reliably for embedded images.
        if (type.includes("msword") || fileName.endsWith(".doc")) {
          return true;
        }
      } catch (error) {
        console.error("Error checking document for images:", error);
        return true;
      }

      return false;
    },
    [pdfContainsImage, docxContainsImage],
  );

  const addOcrErrorLog = useCallback(
    (
      fileName: string,
      reason: string = "Document appears to be a scanned image or contains non-extractable content",
    ) => {
      const newError: OCRErrorLog = {
        fileName,
        reason,
        timestamp: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };

      setOcrErrorLogs((prev) => [...prev, newError]);
      setShowOcrErrors(true);
    },
    [],
  );

  const clearOcrErrorLogs = useCallback(() => {
    setOcrErrorLogs([]);
    setShowOcrErrors(false);
  }, []);

  return {
    ocrErrorLogs,
    showOcrErrors,
    checkDocumentRequiresOCR,
    addOcrErrorLog,
    clearOcrErrorLogs,
  };
};
